import React, { useLayoutEffect, useRef, useState } from 'react';

import { IResellerConfig } from '~/config/shared/reseller-config';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { Col, Cols, Div, getMediaSize, Panel, Panels, Text } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { getDemoPublisherName } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  IHistoryProps,
  IReactChildren,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { backgroundColor } from '~/src/dash/utils/material-dashboard-react';
import { checkUserInfoMasked, hasRoleSuperUser, isSuper } from '~/src/dash/utils/roles';
import { MobileConfig, MobileContext, MobileContextState } from '~/src/dash/views/mobile/contexts';
import { mobileDashboardConfig } from '~/src/dash/views/mobile/MobileBaseView/config';
import { mobileDashboardLiveConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/liveConfig';
import { mobileDashboardOverviewConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/overviewConfig';
import { MobilePublisherSelect } from '~/src/dash/views/mobile/MobileBaseView/MobilePublisherSelect';
import { NavModal } from '~/src/dash/views/mobile/MobileBaseView/NavModal';

const RESELLER_CONFIG = (window as any).CONFIG as IResellerConfig;
const { menuBarText, menuBarBackground } = RESELLER_CONFIG.theme;

interface IProps
  extends IReactChildren,
    IWithStylesProps,
    ISessionStateProps,
    IUserStateProps,
    IReduxActionProps,
    IHistoryProps {}

export const MobileBaseView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
  },
  function (props: IProps) {
    const { classes, children, session, user, location, dispatch } = props;
    const isSuperUser = hasRoleSuperUser(user.user);

    const ref = useRef(null);
    const [size, setSize] = useState([null, null]);
    const [width, height] = size;
    const [isNavOpen, setIsNavOpen] = useState(false);

    const mobileHeight = window.innerHeight * 0.01;

    const { CurrencyType, ExchangeValueToUSD } = session.publisher.ExchangeRate;
    const currencySymbol = convertECurrencyTypeToSymbol(CurrencyType || 0);

    const updateSize = () => {
      // @ts-ignore
      const currentWidth = ref.current ? ref.current.clientWidth : 0;
      // @ts-ignore
      const currentHeight = ref.current ? ref.current.clientHeight : 0;
      setSize([currentWidth, currentHeight]);
    };

    useLayoutEffect(() => {
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, []);

    return (
      <MobileContext.Provider
        value={{
          children,
          classes,
          currencyMultiplier: ExchangeValueToUSD,
          currencySymbol,
          currencyType: CurrencyType,
          dispatch,
          location,
          resellerCfg: RESELLER_CONFIG,
          session,
          user,
          isSuperUser,
        }}
      >
        <MobileContextState.Provider
          value={{
            totalWidth: width,
            totalHeight: height,
            mediaSize: getMediaSize(width),
          }}
        >
          <MobileConfig.Provider
            value={{
              Dashboards: {
                Main: mobileDashboardConfig, // parent
                Overview: mobileDashboardOverviewConfig, // child - overview
                Live: mobileDashboardLiveConfig, // child - live
              },
            }}
          >
            <div
              ref={ref}
              style={{
                position: 'absolute',
                width: '100vw',
                height: `${mobileHeight * 100}px`,
                padding: 0,
                margin: 0,
                flex: '1 1 0%',
                overflowX: 'hidden',
                overflowY: 'auto',
                backgroundColor,
              }}
            >
              <Panels max vertical>
                <Panel
                  style={{
                    backgroundColor: menuBarBackground,
                  }}
                >
                  <Cols
                    verticalPadding
                    smallHorizontalPadding
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    {/* TODO: THIS IS PUBLICA ONLY. UPDATE THIS WHEN WHITELABELS ARE ENABLED */}
                    <Col>
                      <Div
                        onClick={() => setIsNavOpen(true)}
                        style={{
                          display: 'flex',
                          paddingLeft: '8px',
                        }}
                      >
                        <img
                          src='/assets/publica/navbar-logo/publicaIcon.png'
                          width='auto'
                          height='24px'
                        />
                      </Div>
                    </Col>
                    {isSuper(user.user) ? (
                      <Col>
                        <MobilePublisherSelect />
                      </Col>
                    ) : (
                      <Col style={{ padding: '0px 16px' }}>
                        <Text bold color={menuBarText}>
                          {checkUserInfoMasked(user.user)
                            ? getDemoPublisherName()
                            : session.publisher.Name}
                        </Text>
                      </Col>
                    )}
                  </Cols>
                </Panel>
                <Panel maxHeight flex>
                  {children}
                </Panel>
                <Panel>
                  <NavModal
                    open={isNavOpen}
                    handleClose={() => setIsNavOpen(false)}
                    theme={RESELLER_CONFIG.theme}
                  />
                </Panel>
              </Panels>
            </div>
          </MobileConfig.Provider>
        </MobileContextState.Provider>
      </MobileContext.Provider>
    );
  },
);
